import { ref, onMounted, computed, watch } from "vue";
import { config } from "@/config";
import Sidebar from "@/components/Sidebar.vue";
import Header from "@/components/HeaderDashboard.vue";
import { ProductoService } from "@/services/ProductoService";
import { useRouter } from "vue-router";
import Swal from "sweetalert2";
import { hasPermission } from '@/store/userStore';
import { PlusIcon, EditIcon, TrashIcon, TagIcon } from 'lucide-vue-next';
import { formatCurrency } from "@/utils/formatPrice";
export default {
    name: "ProductoAdd",
    components: {
        Sidebar,
        Header,
        PlusIcon,
        EditIcon,
        TrashIcon,
        TagIcon
    },
    setup() {
        const productos = ref([]);
        const isModalOpen = ref(false);
        const isEditing = ref(false);
        const isLoading = ref(false);
        const router = useRouter();
        const currentPage = ref(1); // Página actual
        const itemsPerPage = ref(10); // Número de repuestos por págin
        const searchQuery = ref('');
        formatCurrency;
        watch([searchQuery], () => {
            currentPage.value = 1;
        });
        const processedProductos = computed(() => {
            let list = [...productos.value];
            // 1. Apply search query
            const query = searchQuery.value.toLowerCase().trim();
            if (query) {
                list = list.filter(productos => productos.nombre.toLowerCase().includes(query) ||
                    productos.codigoUnico.toLowerCase().includes(query) ||
                    (productos.codigoBarrasSistema && productos.codigoBarrasSistema.toLowerCase().includes(query)));
            }
            return list;
        });
        const paginatedProductos = computed(() => {
            const list = processedProductos.value;
            const start = (currentPage.value - 1) * itemsPerPage.value;
            const end = start + itemsPerPage.value;
            return list.slice(start, end);
        });
        const totalPages = computed(() => {
            return Math.ceil(processedProductos.value.length / itemsPerPage.value);
        });
        function prevPage() {
            if (currentPage.value > 1) {
                currentPage.value--;
            }
        }
        function nextPage() {
            if (currentPage.value < totalPages.value) {
                currentPage.value++;
            }
        }
        const productoForm = ref({
            id: 0,
            codigoBarrasSistema: "",
            codigoBarrasReferencia: "",
            repuestoID: 4,
            codigoUnico: "",
            detalles: "",
            precioVenta: 0,
            activo: true,
            imagenesDetalle: [],
        });
        const succesToast = Swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
                popup: "success-toast", // Clase personalizada para el toast de error
            },
            didOpen: (toast) => {
                toast.addEventListener("mouseenter", Swal.stopTimer);
                toast.addEventListener("mouseleave", Swal.resumeTimer);
            },
        });
        const errorToast = Swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
                popup: "error-toast", // Clase personalizada para el toast de error
            },
            didOpen: (toast) => {
                toast.addEventListener("mouseenter", Swal.stopTimer);
                toast.addEventListener("mouseleave", Swal.resumeTimer);
            },
        });
        onMounted(async () => {
            await loadProductos();
        });
        async function loadProductos() {
            isLoading.value = true;
            try {
                productos.value = [];
                const response = await ProductoService.getAllProductosDashboard();
                productos.value = response.datos; // Extrae y asigna solo la propiedad 'datos'
                console.log(productos.value);
            }
            catch (error) {
                console.error("Error loading productos:", error);
            }
            isLoading.value = false;
        }
        function openModal() {
            isModalOpen.value = true;
            isEditing.value = false;
            productoForm.value = {
                id: 0,
                codigoBarrasSistema: "",
                codigoBarrasReferencia: "",
                repuestoID: 4,
                codigoUnico: "",
                marcaId: 1,
                color: "",
                detalles: "",
                precioVenta: 0,
                activo: true,
                imagenesDetalle: [],
            };
        }
        function closeModal() {
            isModalOpen.value = false;
            isEditing.value = false;
        }
        const getImagenPortada = (imagenes) => {
            // Buscar la imagen que tenga portada true
            const portada = imagenes.find((imagen) => imagen.portada);
            // Si existe una imagen de portada, retornar su URL con la base de config, sino, retornar una imagen por defecto
            return portada
                ? (portada.imagen.startsWith('http') ? portada.imagen : `${config.API_URL}${portada.imagen}`)
                : require("@/assets/paquete.png");
        };
        function editProducto(id) {
            router.push(`/panel/producto/edit/${id}`);
        }
        // Función para eliminar el producto con confirmación
        async function deleteProducto(id) {
            try {
                const result = await Swal.fire({
                    title: "¿Estás seguro?",
                    text: "No podrás revertir esta acción",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Sí, eliminar",
                    cancelButtonText: "Cancelar",
                    customClass: {
                        confirmButton: "swal-button--confirm-custom",
                        cancelButton: "swal-button--cancel-custom",
                    }
                });
                // Si el usuario confirma la eliminación
                if (result.isConfirmed) {
                    await ProductoService.deleteProducto(id);
                    await loadProductos();
                    succesToast.fire({
                        text: "Producto eliminado correctamente.",
                        icon: "success",
                    });
                }
            }
            catch (error) {
                console.error("Error deleting producto:", error);
                errorToast.fire({
                    text: "Hubo un problema al eliminar el producto",
                    icon: "error",
                });
            }
        }
        async function toggleActivo(id) {
            const result = await Swal.fire({
                title: '¿Cambiar estado del producto?',
                text: "¿Estás seguro de que deseas activar/desactivar este producto?",
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sí, cambiar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#3b82f6',
                cancelButtonColor: '#94a3b8',
            });
            if (!result.isConfirmed)
                return;
            try {
                const response = await ProductoService.toggleEstadoProducto(id); // Envía la actualización al backend
                if (response.exito) {
                    succesToast.fire({
                        text: "Estado de producto actualizado correctamente.",
                        icon: "success",
                    });
                }
            }
            catch (error) {
                console.error("Error updating producto:", error);
                errorToast.fire({
                    text: "Hubo un problema al actualizar el estado del producto",
                    icon: "error",
                });
            }
        }
        function goToAdd() {
            router.push("/panel/producto/add");
        }
        return {
            hasPermission,
            productos,
            isModalOpen,
            isEditing,
            productoForm,
            openModal,
            closeModal,
            editProducto,
            deleteProducto,
            goToAdd,
            getImagenPortada,
            toggleActivo,
            paginatedProductos,
            prevPage,
            nextPage,
            currentPage,
            totalPages,
            isLoading,
            formatCurrency,
            searchQuery
        };
    },
};
